import React from 'react';
import { formatTimeDisplay } from './dateTimeUtils';
import { BARRIER_TYPES } from '../../constants';

/** The editable fields of the barrier add/edit form. */
export interface BarrierFormFields {
  defectId: string;
  type: string;
  start: string;
  end: string;
  notes: string;
}

/**
 * Applies the HH:MM digit mask used by the barrier time inputs: digits only,
 * capped at four, with a colon inserted once the minutes begin.
 */
function maskTimeInput(raw: string): string {
  let digits = raw.replace(/[^0-9]/g, '');
  if (digits.length > 4) digits = digits.substring(0, 4);
  if (digits.length >= 3) digits = `${digits.substring(0, 2)}:${digits.substring(2)}`;
  return digits;
}

interface BarrierDateTimeFieldProps {
  label: string;
  /** Combined `YYYY-MM-DD HH:MM` value; either half may be absent. */
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}

/**
 * Paired date and Zulu-time inputs for one barrier timestamp. The two halves are
 * kept in a single combined string, so each input rewrites its own half and
 * leaves the other untouched.
 */
const BarrierDateTimeField: React.FC<BarrierDateTimeFieldProps> = ({
  label,
  value,
  onChange,
  disabled,
}) => {
  const [datePart = '', timePart = ''] = value.split(' ');
  return (
    <div className="barrier-popup-field">
      <label>{label}</label>
      <div className="barrier-datetime-row">
        <input
          type="date"
          className="field-input barrier-date-input"
          value={datePart}
          onChange={(e) => onChange(timePart ? `${e.target.value} ${timePart}` : e.target.value)}
          disabled={disabled}
        />
        <input
          type="text"
          className="field-input barrier-time-input"
          value={formatTimeDisplay(timePart)}
          onChange={(e) => {
            const maskedTime = maskTimeInput(e.target.value);
            onChange(datePart ? `${datePart} ${maskedTime}` : maskedTime);
          }}
          placeholder="HH:MM"
          maxLength={5}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

/**
 * Props for the barrier add/edit popup.
 */
interface BarrierEditPopupProps {
  /** True when editing an existing barrier rather than adding a new one. */
  isEditing: boolean;
  fields: BarrierFormFields;
  updateFields: (patch: Partial<BarrierFormFields>) => void;
  defects?: { id: number; text: string }[];
  /** Cross-field temporal rule violation, shown in the footer. */
  timeError: string | null;
  canSubmit: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}

/**
 * Add/edit popup for a maintenance barrier. Fields unlock in sequence — defect,
 * then type, then start, then end — so a barrier is never half-attributed.
 */
const BarrierEditPopup: React.FC<BarrierEditPopupProps> = ({
  isEditing,
  fields,
  updateFields,
  defects,
  timeError,
  canSubmit,
  onCancel,
  onSubmit,
}) => {
  const startHasTime: boolean = Boolean(fields.start && fields.start.split(' ')[1]);
  return (
    <div className="barrier-popup-overlay">
      <div className="barrier-popup" onClick={(e) => e.stopPropagation()}>
        <div className="barrier-popup-header">
          <h3>{isEditing ? 'Edit Barrier' : 'New Barrier'}</h3>
          <button className="modal-close" type="button" onClick={onCancel}>
            &times;
          </button>
        </div>
        <div className="barrier-popup-body">
          <div className="barrier-popup-field">
            <label>Associated Defect *</label>
            <select
              className="field-input"
              value={fields.defectId}
              onChange={(e) => updateFields({ defectId: e.target.value })}
            >
              <option value="">Select Defect...</option>
              {defects &&
                defects.map((d) => (
                  <option key={d.id} value={String(d.id)}>
                    {d.id}) {d.text}
                  </option>
                ))}
            </select>
          </div>
          <div className="barrier-popup-field">
            <label>Barrier Type *</label>
            <select
              className="field-input"
              value={fields.type}
              onChange={(e) => updateFields({ type: e.target.value })}
              disabled={!fields.defectId}
            >
              <option value="">Select Type...</option>
              {BARRIER_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <BarrierDateTimeField
            label="Start Time *"
            value={fields.start}
            onChange={(start: string) => updateFields({ start })}
            disabled={!fields.type}
          />
          <BarrierDateTimeField
            label="End Time"
            value={fields.end}
            onChange={(end: string) => updateFields({ end })}
            disabled={!startHasTime}
          />
          <div className="barrier-popup-field">
            <label>Notes</label>
            <textarea
              className="field-textarea"
              value={fields.notes}
              onChange={(e) => updateFields({ notes: e.target.value })}
              rows={3}
              placeholder="Optional notes"
              disabled={!fields.type}
            />
          </div>
        </div>
        <div className="barrier-popup-footer">
          {timeError && (
            <span className="field-inline-error" role="alert">
              {timeError}
            </span>
          )}
          <button className="modal-btn modal-btn-cancel" type="button" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="modal-btn modal-btn-save"
            type="button"
            disabled={!canSubmit}
            onClick={onSubmit}
          >
            {isEditing ? 'Update Barrier' : 'Add Barrier'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BarrierEditPopup;
